
import { Component, Vue, Prop } from 'vue-property-decorator';
import { get, post } from '@/services/http';
import { BillingPackageList } from '@/collections/billing_packages';
import { fetchDiscount } from '@/services/discountService';
import { EventHandler } from '@/modules/events';
import Alert from '@/components/Alert.vue';
@Component({
  components: {
    Alert,
  },
})
export default class StockySwitcher extends Vue {
  @Prop({ default: () => ({}) })
  public store!: any;

  private eventHandler: EventHandler | null = null;

  public offerApplied: boolean = false;
  public checkingOffer: boolean = true;

  public async created() {
    this.eventHandler = new EventHandler({ store: this.store });
    this.eventHandler.track('Stocky Switcher Page Viewed');
    await Promise.all([
      this.checkOfferApplied(),
      this.fetchPricingPackages(),
    ]);
  }

  private async checkOfferApplied() {
    this.checkingOffer = true;
    try {
      const response: any = await get('/stocky/status');
      if (response?.offerApplied) {
        this.offerApplied = true;
      }
      if (response?.verified) {
        this.verified = true;
      }
    } catch (e) {
      console.debug('[StockySwitcher] Failed to check offer status:', e);
    } finally {
      this.checkingOffer = false;
    }
  }

  public apiKey: string = '';
  public loading: boolean = false;
  public checkingStatus: boolean = false;
  public verified: boolean = false;
  public errorMessage: string = '';
  public successMessage: string = '';
  public showVerifyDialog: boolean = false;
  public loadingPackages: boolean = false;
  public selectedPackage: any = null;
  public discount: any = null;

  public valueProps = [
    { icon: 'mdi-swap-horizontal-bold', title: 'Drop-in Stocky replacement', desc: 'Same core features you know — demand forecasting, POs, supplier management' },
    { icon: 'mdi-chart-box-outline', title: '50+ inventory reports', desc: 'Low stock, dead stock, ABC analysis, sell-through, and more' },
    { icon: 'mdi-robot', title: 'Automated purchase orders', desc: 'Set rules and let Assisty generate POs automatically' },
    { icon: 'mdi-store-outline', title: 'Multi-location support', desc: 'Track inventory and suggest transfers across all locations' },
    { icon: 'mdi-email-fast-outline', title: 'Scheduled reports & alerts', desc: 'Get reports delivered to your inbox on your schedule' },
    { icon: 'mdi-puzzle-outline', title: 'Shopify Flow + Google Sheets', desc: 'Integrations Stocky never offered' },
  ];

  public planFeatures = [
    { title: 'Auto migrate data from Stocky', desc: 'Automatically import your suppliers, purchase orders, and tax settings — no manual work needed', stocky: true },
    { title: 'Everything Stocky does, built in', desc: 'Demand forecasting, purchase orders, supplier management — all the features you already rely on', stocky: true },
    { title: 'Multi-location support', desc: 'See stock across all your stores and get smart transfer suggestions to balance inventory', stocky: true },
    { title: '50+ inventory reports', desc: 'Know exactly what to reorder — low stock, dead stock, ABC analysis, sell-through, and more', stocky: false },
    { title: 'Automated purchase orders', desc: 'Stop creating POs manually — set your rules and let Assisty handle reordering for you', stocky: false },
    { title: 'Scheduled reports & alerts', desc: 'Get the numbers you need in your inbox — daily, weekly, or on your own schedule', stocky: false },
    { title: 'Shopify Flow + Google Sheets', desc: 'Connect your inventory data to the tools your team already uses — integrations Stocky never offered', stocky: false },
  ];

  public migrationItems: string[] = [
    'Suppliers & lead times',
    'Purchase orders & history',
    'Tax settings & configurations',
  ];

  public steps = [
    { title: 'Enter your Stocky API key', desc: 'Find it in your Stocky app settings' },
    { title: 'We verify your account', desc: 'Confirm you\'re an active Stocky merchant' },
    { title: 'Claim your exclusive offer', desc: '30-day trial + 50% off for 3 months' },
  ];

  public comparisonFeatures = [
    { feature: 'Demand Forecasting', note: 'Predict future demand by product', stocky: true, assisty: true },
    { feature: 'Purchase Order Management', note: 'Create and track POs', stocky: true, assisty: true },
    { feature: 'Supplier Management', note: 'Manage vendor info and lead times', stocky: true, assisty: true },
    { feature: 'Inventory Reports', note: null, stocky: 'Basic', assisty: '50+ reports', expanded: true,
      details: [
        { name: 'Low Stock Alerts', stocky: true, assisty: true },
        { name: 'Days of Inventory Remaining', stocky: true, assisty: true },
        { name: 'Urgent Reorder Suggestions', stocky: false, assisty: true },
        { name: 'Dead Stock Analysis', stocky: false, assisty: true },
        { name: 'ABC Inventory Classification', stocky: false, assisty: true },
        { name: 'Sell-through Rate', stocky: false, assisty: true },
        { name: 'Inventory Turnover', stocky: false, assisty: true },
        { name: 'Overstock Analysis', stocky: false, assisty: true },
      ],
    },
    { feature: 'Custom Dashboards', note: 'Build your own analytics views', stocky: false, assisty: true },
    { feature: 'Automated Purchase Orders', note: 'Auto-generate POs based on rules', stocky: false, assisty: true },
    { feature: 'Multi-Location Transfers', note: 'Suggest transfers between locations', stocky: true, assisty: true },
    { feature: 'Scheduled Email Reports', note: 'Auto-send reports on a schedule', stocky: false, assisty: true },
    { feature: 'Google Sheets Integration', note: 'Sync data to spreadsheets', stocky: false, assisty: true },
    { feature: 'Shopify Flow Integration', note: 'Trigger automations via Flow', stocky: false, assisty: true },
    { feature: 'Product Segmentation', note: 'ABC analysis and grouping', stocky: false, assisty: true },
    { feature: 'Ongoing Support & Updates', note: null, stocky: 'Ending Aug 2026', assisty: 'Active' },
  ];

  public apiKeyRules: any[] = [
    (v: string) => !!v || 'API key is required',
  ];

  public async openClaimDialog() {
    this.showVerifyDialog = true;
    this.eventHandler?.track('Stocky Switcher Claim Clicked');

    if (!this.verified) {
      this.checkingStatus = true;
      try {
        const response: any = await get('/stocky/status');
        if (response && response.verified) {
          this.verified = true;
          this.successMessage = 'Your Stocky account has been verified.';
          this.eventHandler?.track('Stocky Switcher Already Verified');
          this.fetchPricingPackages();
        }
      } catch {
        // Ignore - just means we can't check status
      } finally {
        this.checkingStatus = false;
      }
    }
  }

  public async verifyKey() {
    if (!this.apiKey) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const response: any = await post('/stocky/verify', { api_key: this.apiKey });

      if (response && response.valid) {
        this.verified = true;
        const storeName = response.store_name ? ` for ${response.store_name}` : '';
        this.successMessage = `Your Stocky account${storeName} has been verified.`;
        this.eventHandler?.track('Stocky Switcher Verification Success', {
          store_name: response.store_name || '',
        });
        this.fetchPricingPackages();
      } else {
        this.eventHandler?.track('Stocky Switcher Verification Failed', { reason: 'invalid_key' });
        this.errorMessage = 'Invalid API key. Please check your Stocky API key and try again.';
      }
    } catch (error: any) {
      this.eventHandler?.track('Stocky Switcher Verification Failed', { reason: 'error' });
      this.errorMessage = error?.message || 'Unable to verify your API key. Please try again later.';
    } finally {
      this.loading = false;
    }
  }

  public get hasActiveSubscription(): boolean {
    return !!(this.store?.currentPackage?.id);
  }

  public get discountedPrice(): number {
    if (!this.selectedPackage || !this.discount) {
      return 0;
    }
    const price = this.selectedPackage.price;
    if (this.discount.type === 'PERCENTAGE_DISCOUNT') {
      return Math.round((price - price * (this.discount.rate / 100)) * 100) / 100;
    }
    return price;
  }

  public get trialDays(): number {
    return this.discount?.package_config?.trialDays || 30;
  }

  public get totalSavings(): number {
    if (!this.selectedPackage || !this.discount) return 0;
    const monthlySaving = this.selectedPackage.price - this.discountedPrice;
    return Math.round(monthlySaving * (this.discount.limitIntervals || 1) * 100) / 100;
  }

  public async fetchPricingPackages() {
    this.loadingPackages = true;
    try {
      const billingPackages = new BillingPackageList();
      await Promise.all([
        billingPackages.fetch(),
        fetchDiscount('r6Mi58').then((d) => { this.discount = d; }),
      ]);

      console.log('[StockySwitcher] Billing packages:', billingPackages.items.map((p: any) => ({ id: p.id, name: p.name, price: p.price, shopifyPlanName: p.shopifyPlanName })));
      console.log('[StockySwitcher] Store assistyPlan:', this.store?.assistyPlan);
      console.log('[StockySwitcher] Discount:', this.discount);

      const paidPackages = billingPackages.items.filter((item: any) => item.price > 0);
      if (this.store && this.store.assistyPlan) {
        const matched = paidPackages.filter((item: any) => item.shopifyPlanName === this.store.assistyPlan);
        if (matched.length > 0) {
          // Pick the highest-priced plan (Replenishment Center)
          this.selectedPackage = matched.sort((a: any, b: any) => b.price - a.price)[0];
        }
      }
      if (!this.selectedPackage && paidPackages.length > 0) {
        // Fallback: pick highest-priced plan overall
        this.selectedPackage = paidPackages.sort((a: any, b: any) => b.price - a.price)[0];
      }
      console.log('[StockySwitcher] Selected package:', this.selectedPackage ? { id: this.selectedPackage.id, name: this.selectedPackage.name, price: this.selectedPackage.price } : null);
    } catch (e) {
      console.error('[StockySwitcher] Failed to fetch pricing:', e);
    } finally {
      this.loadingPackages = false;
    }
  }

  public applyOffer() {
    this.eventHandler?.track('Stocky Switcher Apply Offer Clicked', {
      billing_package_id: this.selectedPackage?.id,
      billing_package_name: this.selectedPackage?.name,
      billing_package_price: this.selectedPackage?.price,
      discounted_price: this.discountedPrice,
    });
    const query: any = { discount: 'r6Mi58' };
    if (this.selectedPackage) {
      query.billing_package_id = this.selectedPackage.id;
    }
    this.$router.push({ path: '/auto_subscription', query });
  }
}
